import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageLoading } from "@/components/common";
import { PostCard, timeAgo } from "@/components/community";

function CommentBubble({ c, canDelete, onDelete }) {
  const a = c.author || {};
  return (
    <div className="rounded-xl bg-secondary/50 p-3">
      <div className="flex items-center justify-between">
        <Link to={`/u/${a.id}`} className="text-xs font-semibold hover:underline">{a.name || "Member"} <span className="font-normal text-muted-foreground">· {timeAgo(c.created_at)}</span></Link>
        {canDelete && <button onClick={() => onDelete(c.id)} data-testid={`comment-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></button>}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">{c.text}</p>
    </div>
  );
}

export default function PostDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const gate = useAuthGate();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sp] = useSearchParams();
  const focusId = sp.get("c");
  const canModerate = user && ["owner", "admin", "moderator"].includes(user.role);
  const canDelete = (c) => user && (c.owner_id === user.id || canModerate);

  const loadPost = useCallback(() => api.get(`/posts/${id}`).then((r) => setPost(r.data)).catch(() => setPost(false)), [id]);
  const loadComments = useCallback(() => api.get(`/posts/${id}/comments`).then((r) => setComments(r.data.items)).catch(() => {}), [id]);
  useEffect(() => { loadPost(); loadComments(); }, [loadPost, loadComments]);

  // Deep-link from a comment notification (?c=<id>): scroll to and briefly highlight it.
  useEffect(() => {
    if (!focusId || comments.length === 0) return;
    const el = document.getElementById(`comment-${focusId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-primary", "ring-offset-2", "ring-offset-background", "rounded-xl");
    const t = setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2", "ring-offset-background"), 2600);
    return () => clearTimeout(t);
  }, [focusId, comments]);

  const addComment = async (parentId, txt) => {
    if (!gate.require("Create a free account to join the conversation.")) return;
    if (!txt || !txt.trim()) return;
    try { await api.post(`/posts/${id}/comments`, { text: txt, parent_id: parentId || null }); await loadComments(); await loadPost(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try { await api.delete(`/comments/${cid}`); await loadComments(); await loadPost(); } catch (e) { toast.error(apiError(e)); }
  };
  const submitTop = () => { addComment(null, text); setText(""); };
  const submitReply = (pid) => { addComment(pid, replyText); setReplyText(""); setReplyTo(null); };

  if (post === false) return <div className="py-20 text-center text-muted-foreground">This post is no longer available.</div>;
  if (!post) return <PageLoading />;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="post-back"><ArrowLeft className="h-4 w-4" />Back</button>
      <PostCard item={post} onChanged={loadPost} onDeleted={() => nav("/app")} />
      <div className="rounded-2xl border border-border bg-card p-4">
        <h2 className="font-display font-semibold">Comments ({post.comment_count || 0})</h2>
        {user && (
          <div className="mt-3 flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment" data-testid="comment-input" />
            <Button onClick={submitTop} data-testid="comment-submit">Send</Button>
          </div>
        )}
        {comments.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">No comments yet. Start the conversation.</p> : (
          <div className="mt-3 space-y-3">
            {comments.map((c) => (
              <div key={c.id} id={`comment-${c.id}`} data-testid={`comment-${c.id}`}>
                <CommentBubble c={c} canDelete={canDelete(c)} onDelete={delComment} />
                {user && <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)} className="ml-3 mt-1 text-xs font-medium text-muted-foreground" data-testid={`comment-reply-btn-${c.id}`}>Reply</button>}
                {replyTo === c.id && (
                  <div className="ml-3 mt-1 flex gap-2">
                    <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a reply" data-testid={`reply-input-${c.id}`} />
                    <Button size="sm" onClick={() => submitReply(c.id)} data-testid={`reply-submit-${c.id}`}>Reply</Button>
                  </div>
                )}
                {(c.replies || []).map((r) => (
                  <div key={r.id} className="ml-8 mt-2" id={`comment-${r.id}`} data-testid={`comment-${r.id}`}>
                    <CommentBubble c={r} canDelete={canDelete(r)} onDelete={delComment} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
